import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Alert, useTheme, useMediaQuery } from "@mui/material";
import Authenticated from "../components/Layouts/Authenticated";
import { useUserProfile } from "../hooks/useUserProfile";
import {
  UserProfileCard,
  SocialLinksCard,
  AboutSection,
  RecipesSection,
  UserProfileSkeleton,
} from "../components/UserProfile";
import { userNavigation } from "../utils/navigation";

/**
 * UserProfilePage Component
 *
 * Displays a comprehensive user profile page with user information,
 * industries & disciplines, social links, about section, and user's recipes.
 * Uses Material-UI for modern, responsive design.
 */
const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Use the custom hook with the userId from the URL
  const { userData, recipes, loading, error } = useUserProfile(userId);

  const handleRecipeClick = (recipeId: string) => {
    // Navigate to recipe details page when available
    navigate(userNavigation.getRecipeUrl(recipeId));
  };

  const handleCategoryClick = (type: string, value: string) => {
    const filters =
      type === "discipline" ? { discipline: value } : { industry: value };
    navigate(userNavigation.getUserDirectoryUrl(filters));
  };

  if (loading) {
    return <UserProfileSkeleton />;
  }

  if (error || !userData) {
    return (
      <Authenticated>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "User not found"}
          </Alert>
        </Container>
      </Authenticated>
    );
  }

  return (
    <Authenticated>
      <title>User - Materiom Commons</title>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            gap: 4,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Left Column - User Info */}
          <Box sx={{ width: { xs: "100%", md: "33%" } }}>
            <Box sx={{ position: isMobile ? "static" : "sticky", top: 24 }}>
              <UserProfileCard
                userData={userData}
                onCategoryClick={handleCategoryClick}
              />
              <SocialLinksCard userData={userData} />
            </Box>
          </Box>

          {/* Right Column - Content */}
          <Box sx={{ width: { xs: "100%", md: "67%" } }}>
            <AboutSection description={userData.description} />
            <RecipesSection
              recipes={recipes}
              onRecipeClick={handleRecipeClick}
            />
          </Box>
        </Box>
      </Container>
    </Authenticated>
  );
};

export default UserProfilePage;
